import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { apiFetch } from '../utils/apiFetch';
import {
  AUTH_SESSION_CHANGING_EVENT, AUTH_SESSION_INVALID_EVENT, AUTH_STORAGE_KEY,
  getAuthToken, getPageAuthSession, getStoredSession, isAdminLike,
} from '../utils/auth';

const PAGE_SIZE = 20;
const emptyFilters = () => ({ order: '', actorId: '', reason: '', start: '', end: '', includeLegacy: false });
const sessionKey = (session) => JSON.stringify([
  session?.token || '', String(session?.user?.companyId || ''), String(session?.user?.id || ''),
  session?.browserSessionId || '', session?.user?.role || '',
]);
const channels = { ifood: 'iFood', balcao: 'Balcão', site: 'Site / WhatsApp', salao: 'Salão', pedidos10: 'Pedidos10', 'brutos-orders': 'Pedidos locais' };
const roles = { gerente: 'Gerente', colaborador: 'Colaborador' };
const money = (value) => typeof value === 'number' && Number.isFinite(value)
  ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const deltaLabel = (value) => typeof value === 'number' && Number.isFinite(value)
  ? `${value > 0 ? '+' : ''}${money(value)}` : '—';
const formattedDate = (value, timeZone = 'America/Sao_Paulo') => {
  const date = new Date(value);
  return value && Number.isFinite(date.getTime()) ? date.toLocaleString('pt-BR', { timeZone }) : 'Não registrado';
};
const localDateValue = (value) => {
  const date = new Date(value);
  if (!value || !Number.isFinite(date.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const dateIntent = (value) => {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) throw new Error('Informe uma data e hora válidas para o período.');
  const [, year, month, day, hour, minute] = match.map(Number);
  const date = new Date(year, month - 1, day, hour, minute);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day
    || date.getHours() !== hour || date.getMinutes() !== minute) throw new Error('Informe uma data e hora válidas para o período.');
  return date.toISOString();
};
const makeQuery = (filters, cursor) => {
  const query = new URLSearchParams({ limit: String(PAGE_SIZE), includeLegacy: String(filters.includeLegacy === true) });
  ['order', 'actorId', 'reason', 'start', 'endExclusive'].forEach((key) => { if (filters[key]) query.set(key, filters[key]); });
  if (cursor) query.set('cursor', cursor);
  return query.toString();
};

const DateTimeFilter = ({ label, value, onChange, disabled }) => {
  const inputRef = useRef(null);
  const hasProgrammaticPicker = typeof HTMLInputElement !== 'undefined'
    && typeof HTMLInputElement.prototype.showPicker === 'function';
  const openCalendar = () => {
    try { if (inputRef.current?.showPicker) inputRef.current.showPicker(); else inputRef.current?.focus(); }
    catch { inputRef.current?.focus(); }
  };
  return <TextField label={label} type="datetime-local" value={value} onChange={onChange} disabled={disabled}
    fullWidth inputRef={inputRef} InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }}
    sx={hasProgrammaticPicker ? { '& input::-webkit-calendar-picker-indicator': { display: 'none' } } : undefined}
    InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" disabled={disabled}
      onClick={openCalendar} aria-label={`Abrir calendário: ${label}`}><CalendarMonthOutlinedIcon /></IconButton></InputAdornment> }} />;
};

const Snapshot = ({ title, snapshot }) => <Box sx={{ flex: 1, minWidth: 0 }}>
  <Typography variant="h6" gutterBottom>{title}</Typography>
  {!snapshot ? <Typography color="text.secondary">Detalhes não registrados.</Typography> : <Stack spacing={1}>
    {(snapshot.items || []).map((item, index) => <Box key={`${item.lineKey || ''}:${index}`}>
      <Typography>{item.quantity} × {item.name}</Typography>
      <Typography variant="body2" color="text.secondary">Unitário: {money(item.unitPrice)} · Total: {money(item.totalPrice)}</Typography>
      {(item.options || []).map((option, optionIndex) => <Typography key={optionIndex} variant="body2">{option.quantity} × {option.name} · {money(option.totalPrice)}</Typography>)}
    </Box>)}
    <Typography>Total: {money(snapshot.total?.orderAmount)}</Typography>
    <Typography variant="body2">Pago líquido: {money(snapshot.payments?.totalPaid)} · Pendente: {money(snapshot.payments?.totalDue)}</Typography>
    <Typography variant="body2">A devolver: {money(snapshot.payments?.refundPendingAmount)}</Typography>
  </Stack>}
</Box>;

const AuditTable = ({ entries, onInspect, timeZone }) => <TableContainer sx={{ overflowX: 'auto' }}>
  <Table size="small" aria-label="Histórico de edições de pedidos" sx={{ minWidth: 900 }}>
    <TableHead><TableRow>{['Pedido e canal', 'Operador', 'Data e hora', 'Motivo', 'Antes', 'Depois', 'Diferença', 'Detalhes'].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
    <TableBody>{entries.map((entry) => <TableRow key={entry.id || `${entry.source}:${entry.orderId}:${entry.revision}`}>
      <TableCell><Typography variant="body2" fontWeight="bold">{entry.publicNumber || entry.orderId}</Typography>
        {entry.publicNumber && <Typography variant="caption" display="block">ID: {entry.orderId}</Typography>}
        <Typography variant="caption" display="block">{channels[entry.source] || entry.source} · Revisão {entry.revision}</Typography></TableCell>
      <TableCell><Typography variant="body2">{entry.actorName || 'Nome não registrado'}</Typography>
        <Typography variant="caption">{roles[entry.actorRole] || 'Perfil histórico não registrado'}</Typography></TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formattedDate(entry.createdAt, timeZone)}</TableCell>
      <TableCell sx={{ minWidth: 180, maxWidth: 300, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.reason || 'Motivo não registrado'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{money(entry.beforeTotal)}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{money(entry.afterTotal)}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{deltaLabel(entry.delta)}</TableCell>
      <TableCell><Button size="small" onClick={() => onInspect(entry)} aria-label={`Ver detalhes do pedido ${entry.publicNumber || entry.orderId}, revisão ${entry.revision}`}>Ver detalhes</Button></TableCell>
    </TableRow>)}</TableBody>
  </Table>
</TableContainer>;

const TenantAuditPanel = ({ apiUrl, session: initialSession }) => {
  const [session] = useState(initialSession);
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [data, setData] = useState(null);
  const [actors, setActors] = useState([]);
  const [cursors, setCursors] = useState(['']);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState({ loading: true, error: '', invalid: false });
  const [selected, setSelected] = useState(null);
  const mounted = useRef(false);
  const sequence = useRef(0);
  const controller = useRef(null);
  const lastRequest = useRef(null);
  const initialSessionKey = sessionKey(session);
  const current = useCallback(() => mounted.current && isAdminLike(session.user)
    && getAuthToken() === session.token && sessionKey(getPageAuthSession()) === initialSessionKey
    && sessionKey(getStoredSession()) === initialSessionKey, [initialSessionKey, session]);

  const load = useCallback(async (filters, cursor = '', pageIndex = 0, history = ['']) => {
    if (!current()) return;
    controller.current?.abort(); const request = new AbortController(); controller.current = request;
    const requestSequence = ++sequence.current;
    lastRequest.current = { filters, cursor, pageIndex, history };
    setStatus({ loading: true, error: '', invalid: false }); setData(null); setSelected(null);
    try {
      const response = await apiFetch(`${String(apiUrl).replace(/\/$/, '')}/api/settings/order-audit?${makeQuery(filters, cursor)}`, {
        cache: 'no-store', signal: request.signal, headers: { Authorization: `Bearer ${session.token}` },
      });
      const body = await response.json();
      if (!current() || sequence.current !== requestSequence || request.signal.aborted) return;
      if (!response.ok || body.success !== true) throw new Error(body.error || 'Não foi possível consultar a auditoria de pedidos.');
      if (!Array.isArray(body.entries) || !Array.isArray(body.actors) || !body.range?.start || !body.range?.endExclusive
        || (body.nextCursor !== null && typeof body.nextCursor !== 'string')
        || body.entries.some((entry) => (!entry.legacy && !roles[entry.actorRole]) || (entry.legacy && !filters.includeLegacy))) throw new Error('A resposta da auditoria está incompleta. Tente consultar novamente.');
      const fixedFilters = { ...filters, start: filters.start || body.range.start, endExclusive: filters.endExclusive || body.range.endExclusive };
      setData(body); setActors(body.actors); setApplied(fixedFilters); setPage(pageIndex); setCursors(history);
      if (!filters.start && !filters.endExclusive) setDraft((before) => ({ ...before,
        start: before.start || localDateValue(body.range.start), end: before.end || localDateValue(body.range.endExclusive) }));
      setStatus({ loading: false, error: '', invalid: false });
    } catch (error) {
      if (error.name !== 'AbortError' && current() && sequence.current === requestSequence) {
        setStatus({ loading: false, error: error.message, invalid: false });
      }
    } finally { if (controller.current === request) controller.current = null; }
  }, [apiUrl, current, session]);

  useEffect(() => {
    mounted.current = true;
    const invalidate = () => {
      controller.current?.abort(); controller.current = null; sequence.current += 1;
      setData(null); setActors([]); setSelected(null); setDraft(emptyFilters());
      setStatus({ loading: false, error: 'A sessão foi alterada. Reabra a auditoria na sessão atual.', invalid: true });
    };
    const checkStorage = (event) => { if ((!event.key || event.key === AUTH_STORAGE_KEY) && !current()) invalidate(); };
    window.addEventListener(AUTH_SESSION_CHANGING_EVENT, invalidate);
    window.addEventListener(AUTH_SESSION_INVALID_EVENT, invalidate);
    window.addEventListener('storage', checkStorage);
    load(emptyFilters());
    return () => {
      mounted.current = false; controller.current?.abort(); sequence.current += 1;
      window.removeEventListener(AUTH_SESSION_CHANGING_EVENT, invalidate);
      window.removeEventListener(AUTH_SESSION_INVALID_EVENT, invalidate);
      window.removeEventListener('storage', checkStorage);
    };
  }, [current, load]);

  const search = (event) => {
    event?.preventDefault(); if (status.invalid || !current()) return;
    try {
      const start = dateIntent(draft.start); const endExclusive = dateIntent(draft.end);
      if (start && endExclusive && new Date(start) >= new Date(endExclusive)) throw new Error('A data e hora final deve ser posterior à inicial.');
      const filters = { order: draft.order.trim(), actorId: draft.actorId, reason: draft.reason.trim(), start, endExclusive, includeLegacy: draft.includeLegacy };
      setApplied(filters); setCursors(['']); setPage(0); load(filters);
    } catch (error) { setStatus((before) => ({ ...before, error: error.message })); }
  };
  const clear = () => { setDraft(emptyFilters()); setApplied(emptyFilters()); setCursors(['']); setPage(0); load(emptyFilters()); };
  const change = (field) => (event) => setDraft((before) => ({ ...before, [field]: event.target.value }));
  const mainEntries = data?.entries.filter((entry) => !entry.legacy) || [];
  const legacyEntries = data?.entries.filter((entry) => entry.legacy) || [];
  const timeZone = data?.range?.timeZone === 'America/Sao_Paulo' ? data.range.timeZone : 'America/Sao_Paulo';
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'horário local';
  const disabled = status.invalid;

  return <Card className="configuracoes-card configuracoes-section"><CardContent>
    <Stack spacing={2}>
      <Typography variant="h6">Auditoria de pedidos</Typography>
      <Typography color="text.secondary">Edições realizadas por gerentes e colaboradores, com os dados registrados no momento da alteração.</Typography>
      <Box component="form" onSubmit={search} sx={{ display: 'grid', gap: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField label="Pedido: ID ou número" value={draft.order} onChange={change('order')} disabled={disabled} fullWidth inputProps={{ maxLength: 255 }} />
          <TextField select label="Usuário" value={draft.actorId} onChange={change('actorId')} disabled={disabled} fullWidth SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
            <option value="">Todos os usuários registrados</option>
            {actors.map((actor) => <option key={actor.id} value={actor.id}>{actor.name || 'Nome não registrado'}{roles[actor.role] ? ` · ${roles[actor.role]}` : ''}</option>)}
          </TextField>
        </Stack>
        <TextField label="Motivo da edição" value={draft.reason} onChange={change('reason')} disabled={disabled} fullWidth inputProps={{ maxLength: 500 }} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <DateTimeFilter label="Data e hora inicial" value={draft.start} onChange={change('start')} disabled={disabled} />
          <DateTimeFilter label="Data e hora final (exclusiva)" value={draft.end} onChange={change('end')} disabled={disabled} />
        </Stack>
        <Typography variant="body2" color="text.secondary">Filtros no horário do aparelho ({localTimeZone}). A data e hora final não fazem parte do período. Sem período informado, são consultados os últimos 30 dias.</Typography>
        {(data?.legacyAvailable || draft.includeLegacy) && <FormControlLabel control={<Checkbox checked={draft.includeLegacy} disabled={disabled}
          onChange={(event) => setDraft((before) => ({ ...before, includeLegacy: event.target.checked }))} />} label="Incluir registros legados sem identificação histórica" />}
        <Stack direction="row" spacing={1} justifyContent="flex-end"><Button disabled={disabled} onClick={clear}>Limpar filtros</Button>
          <Button type="submit" variant="contained" disabled={disabled}>Buscar edições</Button></Stack>
      </Box>
      {status.error && <Alert severity="error" action={!status.invalid && <Button color="inherit" size="small" onClick={() => {
        const last = lastRequest.current || { filters: applied, cursor: cursors[page] || '', pageIndex: page, history: cursors };
        load(last.filters, last.cursor, last.pageIndex, last.history);
      }}>Tentar novamente</Button>}>{status.error}</Alert>}
      {status.loading && <Stack direction="row" alignItems="center" spacing={1} role="status"><CircularProgress size={20} /><Typography>Consultando edições…</Typography></Stack>}
      {data && <>
        <Typography variant="body2" color="text.secondary">Período consultado: {formattedDate(data.range.start, timeZone)} até {formattedDate(data.range.endExclusive, timeZone)} (fim exclusivo). Registros em horário de Brasília.</Typography>
        {mainEntries.length ? <AuditTable entries={mainEntries} onInspect={setSelected} timeZone={timeZone} /> : <Typography>Nenhuma edição de gerente ou colaborador encontrada para estes filtros.</Typography>}
        {legacyEntries.length > 0 && <Box><Typography variant="h6" sx={{ mt: 1 }}>Registros legados</Typography>
          <Alert severity="warning" sx={{ my: 1 }}>Estes registros não comprovam o nome ou o perfil histórico do operador. Dados anteriores não registrados permanecem indisponíveis.</Alert>
          <AuditTable entries={legacyEntries} onInspect={setSelected} timeZone={timeZone} /></Box>}
        {data.legacyAvailable && !applied.includeLegacy && <Typography variant="body2" color="text.secondary">Há registros antigos sem identificação histórica. Use a opção de legado e busque novamente para consultá-los.</Typography>}
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
          <Typography variant="body2">Página {page + 1} · {data.entries.length} registro(s)</Typography>
          <Button disabled={status.loading || page === 0} onClick={() => load(applied, cursors[page - 1], page - 1, cursors)}>Anterior</Button>
          <Button disabled={status.loading || !data.nextCursor} onClick={() => {
            const history = [...cursors.slice(0, page + 1), data.nextCursor]; load(applied, data.nextCursor, page + 1, history);
          }}>Próxima</Button>
        </Stack>
      </>}
    </Stack>
    <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md" aria-labelledby="audit-entry-title">
      <DialogTitle id="audit-entry-title">Detalhes da edição · Pedido {selected?.publicNumber || selected?.orderId} · Revisão {selected?.revision}</DialogTitle>
      <DialogContent dividers><Stack spacing={2}>
        <Typography>{selected?.actorName || 'Nome não registrado'} · {roles[selected?.actorRole] || 'Perfil histórico não registrado'} · {formattedDate(selected?.createdAt, timeZone)}</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>Motivo: {selected?.reason || 'Não registrado'}</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}><Snapshot title="Antes da edição" snapshot={selected?.before} /><Snapshot title="Depois da edição" snapshot={selected?.after} /></Stack>
      </Stack></DialogContent><DialogActions><Button onClick={() => setSelected(null)}>Fechar detalhes</Button></DialogActions>
    </Dialog>
  </CardContent></Card>;
};

export default function OrderAmendmentAuditPanel({ session, ...props }) {
  if (!session?.token || !session?.user?.companyId || !isAdminLike(session.user)) return null;
  const pageSession = getPageAuthSession();
  const captured = pageSession?.token === session.token && String(pageSession?.user?.companyId) === String(session.user.companyId)
    ? { ...pageSession, user: { ...pageSession.user, role: session.user.role } } : session;
  return <TenantAuditPanel key={sessionKey(captured)} session={captured} {...props} />;
}
